<!-- eslint-disable vue/no-use-v-if-with-v-for -->
<template>
  <div class="searchable-select" ref="selectContainer">
    <!-- Search input container -->
    <div class="search-container">
      <!-- Input wrapper for chips and input -->
      <div class="input-wrapper">
        <!-- Selected items as chips inside input -->
        <div
          v-if="hasSelectedItems && props.showChip"
          class="input-chips-container"
          :class="{ 'chips-disabled': disabled }"
        >
          <!-- Multiple selection chips - show first + ellipsis if more -->
          <template v-if="multiple">
            <!-- First chip -->
            <CustomChip
              v-if="selectedItems.length > 0"
              :label="selectedItems[0].label"
              size="small"
              :removable="!disabled"
              @remove="removeItem(selectedItems[0].value)"
              class="input-chip"
            />
            <!-- Ellipsis indicator if more than one -->
            <span
              v-if="selectedItems.length > 1"
              class="chips-ellipsis"
              :title="`+${selectedItems.length - 1} autres sélections`"
            >
              +{{ selectedItems.length - 1 }}
            </span>
          </template>

          <!-- Single selection chip -->
          <CustomChip
            v-else-if="selectedValue"
            :label="getSelectedLabel()"
            size="small"
            :removable="!disabled"
            @remove="clearSelection"
            class="input-chip"
          />
        </div>

        <!-- Search input -->
        <input
          v-model="searchText"
          @input="handleSearch(searchText)"
          @focus="handleFocus"
          @blur="handleBlur"
          :disabled="disabled"
          :placeholder="dynamicPlaceholder"
          class="search-input"
          :class="{
            'has-chips': hasSelectedItems && props.showChip,
          }"
        />
      </div>

      <!-- Loader -->
      <div v-if="isLoading" class="loader-container" aria-label="Loading">
        <div class="spinner"></div>
      </div>

      <!-- Clear button -->
      <button
        v-if="searchText && !disabled && !isLoading"
        @click="clearSearch"
        class="clear-button"
        type="button"
        aria-label="Clear search"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </button>
    </div>
  </div>

  <!-- Dropdown: inline mode (renders in document flow, pushes content down) -->
  <Transition name="dropdown-expand">
    <ul v-if="inline && showDropdown" ref="dropdownRef" class="options-dropdown-inline">
      <li v-if="filteredOptions.length === 0" class="option-item option-empty">
        <span class="option-empty-text">{{ t('common.noResults') }}</span>
      </li>
      <li
        v-for="option in filteredOptions"
        :key="option.value"
        @click="selectOption(option)"
        :class="{
          'option-disabled': !props.multiple && isOptionSelected(option.value),
          'option-selected': props.multiple && isOptionSelected(option.value),
        }"
        class="option-item"
      >
        <div class="option-content">
          <div class="option-text">
            <h3 class="option-title">{{ option.label }}</h3>
            <p v-if="option.subtitle" class="option-subtitle">
              {{ option.subtitle }}
            </p>
          </div>
          <div
            v-if="props.multiple"
            class="option-checkbox"
            :class="{ checked: isOptionSelected(option.value) }"
          >
            <svg v-if="isOptionSelected(option.value)" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </div>
        </div>
      </li>
    </ul>
  </Transition>

  <!-- Dropdown: teleported mode (default, overlays content) -->
  <Teleport to="body">
    <ul
      v-if="!inline && showDropdown"
      ref="dropdownRef"
      class="options-dropdown-teleported"
      :style="dropdownStyle"
    >
      <li v-if="filteredOptions.length === 0" class="option-item option-empty">
        <span class="option-empty-text">{{ t('common.noResults') }}</span>
      </li>
      <li
        v-for="option in filteredOptions"
        :key="option.value"
        @click="selectOption(option)"
        :class="{
          'option-disabled': !props.multiple && isOptionSelected(option.value),
          'option-selected': props.multiple && isOptionSelected(option.value),
        }"
        class="option-item"
      >
        <div class="option-content">
          <div class="option-text">
            <h3 class="option-title">{{ option.label }}</h3>
            <p v-if="option.subtitle" class="option-subtitle">
              {{ option.subtitle }}
            </p>
          </div>
          <!-- Checkbox for items in multiple mode -->
          <div
            v-if="props.multiple"
            class="option-checkbox"
            :class="{ checked: isOptionSelected(option.value) }"
          >
            <svg v-if="isOptionSelected(option.value)" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </svg>
          </div>
        </div>
      </li>
    </ul>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useLocale } from '@/common/composables/useLocale';
import CustomChip from '../chip/CustomChip.vue';
import { logger } from '@/common/services/config';

// Types
interface Option {
  label: string;
  value: any;
  subtitle?: string;
}

interface Props {
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  maxSelections?: number;
  debounce?: number;
  searchFunction?: (query: string) => Promise<Option[]>;
  modelValue?: any;
  selectedItems?: Option[];
  showChip?: boolean;
  loading?: boolean;
  inline?: boolean;
}

const { t } = useLocale();

interface Emits {
  (e: 'update:modelValue', value: any): void;
  (e: 'update:selectedItems', items: Option[]): void;
  (e: 'search', query: string): void;
  (e: 'select', option: Option): void;
  (e: 'remove', value: any): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  multiple: false,
  maxSelections: undefined,
  debounce: 300,
  selectedItems: () => [],
  showChip: false,
  loading: false,
  inline: false,
});

const emit = defineEmits<Emits>();

// Reactive state
const searchText = ref('');
const showDropdown = ref(false);
const internalOptions = ref<Option[]>([]);
const isUserTyping = ref(false);
const isInputFocused = ref(false);
const isSearching = ref(false);
const selectContainer = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const dropdownPosition = ref({ top: 0, left: 0, width: 0 });

// Computed style for teleported dropdown
const dropdownStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${dropdownPosition.value.top}px`,
  left: `${dropdownPosition.value.left}px`,
  width: `${dropdownPosition.value.width}px`,
  zIndex: 99999,
}));

// Update dropdown position based on input element
const updateDropdownPosition = () => {
  if (!selectContainer.value) return;
  const rect = selectContainer.value.getBoundingClientRect();
  dropdownPosition.value = {
    top: rect.bottom + 4, // 4px gap
    left: rect.left,
    width: rect.width,
  };
};

// Computed
const filteredOptions = computed(() => {
  if (props.searchFunction) {
    if (!searchText.value && props.options.length > 0) {
      return props.options.slice(0, 10);
    }
    return internalOptions.value;
  }

  if (!searchText.value) {
    return props.options.slice(0, 10);
  }

  return props.options
    .filter(
      (option) =>
        option.label.toLowerCase().includes(searchText.value.toLowerCase()) ||
        (option.subtitle && option.subtitle.toLowerCase().includes(searchText.value.toLowerCase()))
    )
    .slice(0, 10);
});

const selectedValue = computed(() => props.modelValue);
const selectedItems = computed(() => props.selectedItems);

// Check if we have selected items
const hasSelectedItems = computed(() => {
  if (props.multiple) {
    return selectedItems.value.length > 0;
  }
  return selectedValue.value !== null && selectedValue.value !== undefined;
});

// Dynamic placeholder that hides when value is selected
const dynamicPlaceholder = computed(() => {
  // Show max selections reached message
  if (props.multiple && props.maxSelections && selectedItems.value.length >= props.maxSelections) {
    return t('maximumSelectionsReached', { count: props.maxSelections });
  }

  // Hide placeholder when we have selected items with chips shown and not actively typing or focused
  if (
    hasSelectedItems.value &&
    props.showChip &&
    !isUserTyping.value &&
    !isInputFocused.value &&
    !searchText.value
  ) {
    return '';
  }

  return props.placeholder;
});

// Methods
const handleFocus = () => {
  isInputFocused.value = true;
  // Show dropdown with available options when focusing
  if (!props.disabled && props.options.length > 0) {
    showDropdown.value = true;
    updateDropdownPosition();
  }
};

const handleBlur = () => {
  // Small delay to allow click on dropdown options before hiding
  setTimeout(() => {
    isInputFocused.value = false;
  }, 200);
};

// Combined loading state: external prop OR internal search in progress
const isLoading = computed(() => props.loading || isSearching.value);

const handleSearch = async (value: string) => {
  isUserTyping.value = true;
  searchText.value = value;
  showDropdown.value = value.length > 0;

  emit('search', value);

  const minSearchLength = props.options.length > 0 ? 1 : 2;
  if (props.searchFunction && value.length >= minSearchLength) {
    isSearching.value = true;
    try {
      internalOptions.value = await props.searchFunction(value);
    } catch (error) {
      logger.error('Error in search function:', error);
      internalOptions.value = [];
    } finally {
      isSearching.value = false;
    }
  }
};

const clearSearch = () => {
  isUserTyping.value = true;
  searchText.value = '';

  // For multiple selection, keep dropdown open if there are no selected items
  // For single selection, close dropdown
  if (props.multiple && selectedItems.value.length === 0) {
    showDropdown.value = true;
  } else {
    showDropdown.value = false;
  }

  emit('search', '');
};

const selectOption = (option: Option) => {
  emit('select', option);
  isUserTyping.value = false;

  if (props.multiple) {
    const isAlreadySelected = selectedItems.value.some((item) => item.value === option.value);

    if (isAlreadySelected) {
      // Deselect: remove the item
      const newItems = selectedItems.value.filter((item) => item.value !== option.value);
      emit('update:selectedItems', newItems);
      emit('remove', option.value);
    } else {
      // Check max selections limit
      if (props.maxSelections && selectedItems.value.length >= props.maxSelections) {
        return; // Don't add more items if limit reached
      }

      const newItems = [...selectedItems.value, option];
      emit('update:selectedItems', newItems);
    }

    // Clear search text to allow new search
    searchText.value = '';
    // Keep dropdown open for multiple selection
    showDropdown.value = true;
    // Recalculate dropdown position after DOM update (external chips may shift layout)
    nextTick(() => updateDropdownPosition());
  } else {
    emit('update:modelValue', option.value);
    searchText.value = option.subtitle ? `${option.label} (${option.subtitle})` : option.label;
    showDropdown.value = false;
  }
};

const removeItem = (value: any) => {
  if (props.disabled) return;

  emit('remove', value);

  if (props.multiple) {
    const newItems = selectedItems.value.filter((item) => item.value !== value);
    emit('update:selectedItems', newItems);
  }
};

const clearSelection = () => {
  if (props.disabled) return;

  isUserTyping.value = false;
  emit('update:modelValue', null);
  searchText.value = '';
};

const isOptionSelected = (value: any): boolean => {
  if (props.multiple) {
    return selectedItems.value.some((item) => item.value === value);
  }
  return selectedValue.value === value;
};

const getSelectedLabel = (): string => {
  if (props.multiple) return '';

  // First try to find the option in the provided options
  let option = props.options.find((opt) => opt.value === selectedValue.value);

  // If not found and we have internal options (from search), try there
  if (!option && internalOptions.value.length > 0) {
    option = internalOptions.value.find((opt) => opt.value === selectedValue.value);
  }

  if (!option) return '';

  return option.subtitle ? `${option.label} (${option.subtitle})` : option.label;
};

// Watch for external changes
watch(
  () => props.modelValue,
  (newValue) => {
    // Only update searchText if user is not currently typing
    if (!isUserTyping.value) {
      if (!props.multiple && newValue) {
        // Find the option in options, internalOptions, or selectedItems
        let option = props.options.find((opt) => opt.value === newValue);
        if (!option && internalOptions.value.length > 0) {
          option = internalOptions.value.find((opt) => opt.value === newValue);
        }
        // Check in selectedItems as well for pre-selected values
        if (!option && props.selectedItems && props.selectedItems.length > 0) {
          option = props.selectedItems.find((opt) => opt.value === newValue);
        }
        if (option) {
          searchText.value = option.subtitle
            ? `${option.label} (${option.subtitle})`
            : option.label;
        }
      } else if (!props.multiple && !newValue) {
        searchText.value = '';
      }
    }
  },
  { immediate: true }
);

// Watch for selectedItems changes
watch(
  () => props.selectedItems,
  (newSelectedItems) => {
    // Only update searchText if user is not currently typing
    if (
      !isUserTyping.value &&
      !props.multiple &&
      props.modelValue &&
      newSelectedItems &&
      newSelectedItems.length > 0
    ) {
      const option = newSelectedItems.find((opt) => opt.value === props.modelValue);
      if (option) {
        searchText.value = option.subtitle ? `${option.label} (${option.subtitle})` : option.label;
      }
    }
  },
  { immediate: true }
);

// Watch showDropdown to update position
watch(showDropdown, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

// Click outside to close dropdown
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  const isInsideContainer = selectContainer.value?.contains(target);
  const isInsideDropdown = dropdownRef.value?.contains(target);

  if (!isInsideContainer && !isInsideDropdown) {
    showDropdown.value = false;
  }
};

// Handle scroll to update dropdown position
const handleScroll = () => {
  if (showDropdown.value) {
    updateDropdownPosition();
  }
};

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('mousedown', handleClickOutside);
  window.addEventListener('scroll', handleScroll, true);
  window.addEventListener('resize', updateDropdownPosition);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('scroll', handleScroll, true);
  window.removeEventListener('resize', updateDropdownPosition);
});
</script>

<style scoped>
.searchable-select {
  position: relative;
  width: 100%;
  z-index: 1; /* Ensure base component has stacking context */
}

/* Search input styling */
.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--ion-text-color);
  min-width: 120px;
  padding: 0;
}

.search-input::placeholder {
  color: var(--ion-text-color-secondary);
}

.search-input:disabled {
  background: transparent;
  color: var(--ion-input-background-color-disabled);
  cursor: not-allowed;
}

/* Hide input text when showing chips */
.search-input.has-chips {
  color: transparent;
}

/* Hide input text when value is selected but not showing chips */
.search-input.has-selected-value {
  color: transparent;
}

/* Disabled input wrapper */
.input-wrapper:has(.search-input:disabled) {
  background: var(--ion-input-background-color-disabled);
  border-color: var(--ion-input-border-color-disabled);
  cursor: not-allowed;
}

/* Search container styling */
.search-container {
  position: relative;
}

/* Input wrapper with overflow hidden for chips */
.input-wrapper {
  position: relative;
  border: 1px solid var(--ion-input-border-color);
  border-radius: var(--ion-input-border-radius);
  background: var(--ion-input-background-color);
  transition: all 0.3s ease;
  overflow: hidden;
  height: var(--ion-input-height, 40px);
  display: flex;
  align-items: center; /* Center align instead of flex-start */
  padding: 6px 12px;
  gap: 6px;
}

.input-wrapper:focus-within {
  border-color: var(--ion-input-border-color-focus);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Chips container inside input */
.input-chips-container {
  display: flex;
  flex-wrap: nowrap; /* Don't wrap to next line */
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
  max-width: calc(100% - 140px); /* Leave space for input and clear button */
  overflow: hidden; /* Hide overflow chips */
}

.input-chips-container::-webkit-scrollbar {
  display: none;
}

.chips-disabled .input-chip {
  opacity: 0.6;
  pointer-events: none;
}

/* Ellipsis indicator for overflow chips */
.chips-ellipsis {
  font-size: 11px;
  font-weight: 500;
  color: #4b5563;
  padding: 4px 6px;
  background: var(--ion-input-background-color);
  border: 1px solid var(--ion-input-border-color);
  border-radius: 12px;
  flex-shrink: 0;
  cursor: default;
  user-select: none;
}

/* Clear button */
.clear-button {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  color: var(--ion-text-color-secondary);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.clear-button:hover {
  background: var(--ion-input-background-hover);
  color: var(--ion-text-color-contrast);
}

.clear-button svg {
  width: 16px;
  height: 16px;
}

/* Loader styling */
.loader-container {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--ion-input-background-color);
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Input value display (when chips are disabled) */
.input-value-display {
  position: absolute;
  top: 50%;
  left: 16px;
  right: 50px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 10;
}

.selected-count,
.selected-value-text {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
}

/* Dropdown styling */
.options-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  border: 1px solid var(--ion-input-border-color);
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
  background: var(--ion-background-color);
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.4),
    0 4px 6px -2px rgba(0, 0, 0, 0.2);
  z-index: 9999; /* Very high z-index to appear above cards */
  animation: dropdownSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  list-style: none;
  padding: 0;
  margin: 0;
}

@keyframes dropdownSlideIn {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.option-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.option-item:last-child {
  border-bottom: none;
}

.option-empty {
  cursor: default;
  text-align: center;
  padding: 16px;
}

.option-empty-text {
  font-size: 14px;
  color: var(--ion-text-color-secondary);
  font-style: italic;
}

.option-item:hover {
  background: var(--ion-input-background-hover-secondary);
}

.option-item.option-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--ion-input-background-color-disabled);
}

.option-item.option-selected {
  /* background: var(--ion-color-primary); */
  border-color: var(--ion-input-border-color-focus);
  color: var(--ion-text-color-contrast);
}

.option-item.option-selected:hover {
  background: var(--ion-input-background-color-selected-hover);
}

.option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option-checkbox {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid var(--ion-input-border-color);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  background: transparent;
}

.option-checkbox.checked {
  background: var(--ion-color-primary);
  border-color: var(--ion-color-primary);
}

.option-checkbox svg {
  width: 14px;
  height: 14px;
  color: var(--ion-text-color-contrast);
}

.option-title {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--ion-text-color);
}

/* .option-selected .option-title {
  color: var(--ion-text-color-contrast);
} */

.option-subtitle {
  margin: 0;
  font-size: 12px;
  color: var(--ion-text-color-secondary);
}

/* Scrollbar styling */
.options-dropdown::-webkit-scrollbar {
  width: 6px;
}

.options-dropdown::-webkit-scrollbar-track {
  background: var(--ion-color-primary);
  border-radius: 8px;
}

.options-dropdown::-webkit-scrollbar-thumb {
  background: var(--ion-color-primary);
  border-radius: 8px;
}

.options-dropdown::-webkit-scrollbar-thumb:hover {
  background: var(--ion-color-secondary-light);
}

/* Inline dropdown (renders in document flow) */
.options-dropdown-inline {
  border: 1px solid var(--ion-input-border-color);
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  background: var(--ion-background-color);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
}

/* Transition for inline dropdown expand/collapse */
.dropdown-expand-enter-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.dropdown-expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.dropdown-expand-enter-from {
  opacity: 0;
  max-height: 0;
}

.dropdown-expand-enter-to {
  opacity: 1;
  max-height: 250px;
}

.dropdown-expand-leave-from {
  opacity: 1;
  max-height: 250px;
}

.dropdown-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .input-wrapper {
    height: 36px; /* Fixed height for mobile */
    padding: 4px 8px;
  }

  .search-input {
    font-size: 14px;
    min-width: 80px;
  }

  .input-chips-container {
    gap: 2px;
    max-width: calc(100% - 120px);
  }

  .clear-button svg {
    width: 14px;
    height: 14px;
  }
}
</style>

<!-- Global styles for teleported dropdown (cannot be scoped) -->
<style>
.options-dropdown-teleported {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  background: #ffffff;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  animation: dropdownSlideInTeleport 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  list-style: none;
  padding: 0;
  margin: 0;
}

@keyframes dropdownSlideInTeleport {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.options-dropdown-teleported .option-item {
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f3f4f6;
}

.options-dropdown-teleported .option-item:last-child {
  border-bottom: none;
}

.options-dropdown-teleported .option-item:hover {
  background-color: #f3f4f6;
}

.options-dropdown-teleported .option-item:active {
  background-color: #e5e7eb;
}

.options-dropdown-teleported .option-empty {
  cursor: default;
  text-align: center;
  padding: 16px;
}

.options-dropdown-teleported .option-empty-text {
  font-size: 14px;
  color: #6b7280;
  font-style: italic;
}

.options-dropdown-teleported .option-disabled {
  opacity: 0.5;
  pointer-events: none;
  background-color: #f9fafb;
}

.options-dropdown-teleported .option-selected {
  background-color: #eff6ff;
}

.options-dropdown-teleported .option-selected:hover {
  background-color: #dbeafe;
}

.options-dropdown-teleported .option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.options-dropdown-teleported .option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.options-dropdown-teleported .option-checkmark {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: #3b82f6;
}

.options-dropdown-teleported .option-checkmark svg {
  width: 100%;
  height: 100%;
}

.options-dropdown-teleported .option-title {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.options-dropdown-teleported .option-subtitle {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

/* Scrollbar styling for teleported dropdown */
.options-dropdown-teleported::-webkit-scrollbar {
  width: 6px;
}

.options-dropdown-teleported::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 8px;
}

.options-dropdown-teleported::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 8px;
}

.options-dropdown-teleported::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
